// components/EditPlanModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Calendar, Percent, Check, Lock, Shield, Save, ChevronDown, ChevronUp, Eye, Gift, AlertCircle, Loader2 } from 'lucide-react';

const EditPlanModal = ({ 
  isOpen, 
  onClose, 
  plan, 
  onSave, 
  isSaving,
  modulesList,
  rolesList,
  error
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'paid',
    annualPrice: 0,
    taxName: 'GST',
    taxPercentage: 0,
    isTaxInclusive: false,
    maxStudents: 0,
    maxStaff: 0,
    maxBranches: 1,
    maxAdmins: 1,
    maxStorageGB: 5,
    isActive: true,
    isPublic: true,
    visibility: 'public',
    assignedTenants: [],
    invitedTenants: [],
    trialEnabled: false,
    trialDays: 14,
    modules: {},
    roleAccess: {}
  });

  const [expandedSection, setExpandedSection] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);

  // Get role keys from plan or rolesList
  const roleKeys = React.useMemo(() => {
    if (Array.isArray(rolesList) && rolesList.length > 0) {
      return rolesList;
    }
    const fromAccess = Object.keys(formData.roleAccess || {});
    return fromAccess.length > 0 ? fromAccess : ['admin', 'manager', 'staff', 'teacher', 'student'];
  }, [rolesList, formData.roleAccess]);

  const enabledRolesCount = roleKeys.filter((key) => Boolean(formData.roleAccess?.[key])).length;

  // Get module keys from plan or modulesList
  const moduleKeys = React.useMemo(() => {
    const fromModules = Object.keys(formData.modules || {});

    if (Array.isArray(modulesList) && modulesList.length > 0) {
      const merged = Array.from(new Set([...modulesList, ...fromModules]));
      return merged;
    }

    if (modulesList && typeof modulesList === 'object') {
      const flattened = Object.values(modulesList)
        .flatMap((group) => (Array.isArray(group) ? group : []));
      const merged = Array.from(new Set([...flattened, ...fromModules]));
      if (merged.length > 0) {
        return merged;
      }
    }

    return fromModules.length > 0
      ? fromModules
      : ['dashboard', 'settings', 'studentManagement', 'staffManagement', 'classliveview', 'websiteManagement', 'lmsmanagement', 'reports'];
  }, [modulesList, formData.modules]);

  const moduleCount = Object.values(formData.modules).filter(v => v).length;

  // Fetch PlanLimits when plan changes
  useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!plan || !isOpen) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/super-admin/subscription/plans/${plan.planId}/limits`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setPlanLimits(data.data);
            console.log('📝 [EditPlanModal] Loaded PlanLimits:', data.data);
          }
        }
      } catch (err) {
        console.error('❌ Error fetching PlanLimits:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlanLimits();
  }, [plan, isOpen]);

  useEffect(() => {
    if (plan && isOpen) {
      console.log('📝 [EditPlanModal] Loading plan data:', plan);
      
      const annualAmount = plan.pricing?.yearly?.amount || plan.pricing?.monthly?.amount * 12 || 0;
      
      // If we have PlanLimits, use those for modules and roleAccess
      let modules = plan.modules || {};
      let roleAccess = plan.roleAccess || {};
      
      if (planLimits) {
        if (planLimits.modules instanceof Map) {
          modules = Object.fromEntries(planLimits.modules);
        } else if (planLimits.modules) {
          modules = planLimits.modules;
        }
        
        if (planLimits.roleAccess instanceof Map) {
          roleAccess = Object.fromEntries(planLimits.roleAccess);
        } else if (planLimits.roleAccess) {
          roleAccess = planLimits.roleAccess;
        }
      }
      
      console.log('📝 [EditPlanModal] Modules from plan:', modules);
      console.log('📝 [EditPlanModal] Roles from plan:', roleAccess);
      
      // ✅ FIX: Only use enabled flag to determine trial status
      const trialEnabled = plan.trial?.enabled === true;
      // ✅ FIX: Only show durationDays if trial is enabled
      const trialDays = trialEnabled ? (plan.trial?.durationDays || 14) : 14;
      
      console.log('📝 [EditPlanModal] Trial data:', {
        enabled: trialEnabled,
        durationDays: plan.trial?.durationDays,
        trialDays: trialDays,
        raw: plan.trial
      });
      
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        type: plan.type || 'paid',
        annualPrice: annualAmount,
        taxName: plan.tax?.taxName || 'GST',
        taxPercentage: plan.tax?.taxPercentage || 0,
        isTaxInclusive: plan.tax?.isTaxInclusive || false,
        maxStudents: plan.features?.maxStudents || 0,
        maxStaff: plan.features?.maxStaff || 0,
        maxBranches: plan.features?.maxBranches || 1,
        maxAdmins: plan.features?.maxAdmins || 1,
        maxStorageGB: plan.features?.maxStorageGB || 5,
        isActive: plan.isActive !== undefined ? plan.isActive : true,
        isPublic: plan.isPublic !== undefined ? plan.isPublic : true,
        visibility: plan.visibility || 'public',
        assignedTenants: plan.assignedTenants || [],
        invitedTenants: plan.invitedTenants || [],
        trialEnabled: trialEnabled,
        trialDays: trialDays,
        modules: modules,
        roleAccess: roleAccess
      });
    }
  }, [plan, isOpen, planLimits]);

  if (!isOpen || !plan) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('📝 [EditPlanModal] handleChange:', { name, value, type, checked });
    
    setFormData(prev => {
      const newState = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // ✅ If trialEnabled is unchecked, reset trialDays to 0 or default
      if (name === 'trialEnabled' && !checked) {
        newState.trialDays = 14; // Keep default value for when re-enabled
      }
      
      console.log('📝 [EditPlanModal] New state:', newState);
      return newState;
    });
  };

  const handleModuleToggle = (moduleName) => {
    setFormData(prev => {
      const newModules = {
        ...prev.modules,
        [moduleName]: !prev.modules[moduleName]
      };
      console.log('📝 [EditPlanModal] Toggled module:', moduleName, 'New state:', newModules);
      return {
        ...prev,
        modules: newModules
      };
    });
  };

  const handleRoleToggle = (roleName) => {
    setFormData(prev => {
      const newRoles = {
        ...prev.roleAccess,
        [roleName]: !prev.roleAccess[roleName]
      };
      console.log('📝 [EditPlanModal] Toggled role:', roleName, 'New state:', newRoles);
      return {
        ...prev,
        roleAccess: newRoles
      };
    });
  };

// In EditPlanModal.jsx - handleSubmit
const handleSubmit = (e) => {
  e.preventDefault();
  
  // ✅ FIX: Calculate trial values properly
  const trialEnabled = formData.trialEnabled === true;
  const trialDays = parseInt(formData.trialDays) || 14;
  
  // ✅ If trial is enabled, use the specified days; if disabled, use 0
  const finalTrialDays = trialEnabled ? (trialDays > 0 ? trialDays : 14) : 0;
  
  console.log('📝 [EditPlanModal] Trial submission:', {
    trialEnabled,
    trialDays,
    finalTrialDays,
    willBeEnabled: trialEnabled && finalTrialDays > 0
  });
  
  const saveData = {
    name: formData.name,
    description: formData.description,
    type: formData.type,
    annualPrice: parseFloat(formData.annualPrice) || 0,
    tax: {
      taxName: formData.taxName || 'GST',
      taxPercentage: parseFloat(formData.taxPercentage) || 0,
      isTaxInclusive: formData.isTaxInclusive
    },
    features: {
      maxStudents: parseInt(formData.maxStudents) || 0,
      maxStaff: parseInt(formData.maxStaff) || 0,
      maxBranches: parseInt(formData.maxBranches) || 1,
      maxAdmins: parseInt(formData.maxAdmins) || 1,
      maxStorageGB: parseInt(formData.maxStorageGB) || 5
    },
    isActive: formData.isActive,
    isPublic: formData.isPublic,
    visibility: formData.visibility,
    assignedTenants: formData.assignedTenants || [],
    invitedTenants: formData.invitedTenants || [],
    // ✅ Send complete trial object
    trial: {
      enabled: trialEnabled && finalTrialDays > 0,
      durationDays: finalTrialDays,
      featuresDuringTrial: 'limited',
      maxTrialsPerTenant: 1,
      limitedFeatures: {
        maxStudents: 5,
        maxStaff: 2,
        maxBranches: 1,
        maxAdmins: 1,
        maxStorageGB: 1,
        maxCourses: 3,
        maxClasses: 5,
        maxExams: 2
      },
      trialModules: {
        dashboard: true,
        settings: true,
        studentManagement: true,
        staffManagement: true,
        classliveview: true,
        websiteManagement: false,
        lmsmanagement: false,
        feeManagement: false,
        reports: false
      }
    },
    modules: formData.modules || {},
    roleAccess: formData.roleAccess || {}
  };

  console.log('📝 [EditPlanModal] Trial being saved:', saveData.trial);
  onSave(saveData);
};

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const calculateTaxBreakdown = () => {
    const price = parseFloat(formData.annualPrice) || 0;
    const taxRate = parseFloat(formData.taxPercentage) || 0;
    if (price === 0 || taxRate === 0) return null;

    if (formData.isTaxInclusive) {
      const baseAmount = price / (1 + taxRate / 100);
      const taxAmount = price - baseAmount;
      return { type: 'inclusive', totalPrice: price, baseAmount, taxAmount, taxRate, taxName: formData.taxName || 'GST' };
    } else {
      const baseAmount = price;
      const taxAmount = price * (taxRate / 100);
      const totalPrice = price + taxAmount;
      return { type: 'exclusive', totalPrice, baseAmount, taxAmount, taxRate, taxName: formData.taxName || 'GST' };
    }
  };

  const taxBreakdown = calculateTaxBreakdown();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Edit Plan: {plan.name}</h2>
            <p className="text-sm text-slate-500">
              {loading ? 'Loading...' : `${moduleCount} modules • ${enabledRolesCount} roles`}
              {planLimits && ` • v${planLimits.version}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              <span className="ml-3 text-sm text-slate-500">Loading plan limits...</span>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700 font-medium">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <button 
                type="button"
                onClick={() => {}} 
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('basic')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Calendar className="w-4 h-4 text-indigo-500" />
                Basic Information
              </span>
              {expandedSection === 'basic' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'basic' && (
              <div className="px-4 pb-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Plan Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    >
                      <option value="free">Free</option>
                      <option value="paid">Paid</option>
                      <option value="enterprise">Enterprise</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    rows="2"
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pricing Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('pricing')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Percent className="w-4 h-4 text-emerald-500" />
                Pricing & Tax
              </span>
              {expandedSection === 'pricing' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'pricing' && (
              <div className="px-4 pb-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Annual Price (₹)</label>
                    <input
                      type="number"
                      name="annualPrice"
                      min="0"
                      step="1"
                      value={formData.annualPrice}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tax Name</label>
                    <input
                      type="text"
                      name="taxName"
                      value={formData.taxName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      placeholder="e.g., GST"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tax Percentage (%)</label>
                    <input
                      type="number"
                      name="taxPercentage"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.taxPercentage}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isTaxInclusive"
                        checked={formData.isTaxInclusive}
                        onChange={handleChange}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                      />
                      Price includes tax
                    </label>
                  </div>
                </div>

                {taxBreakdown && (
                  <div className={`p-3 rounded-lg border ${
                    taxBreakdown.type === 'inclusive' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className={`text-xs ${taxBreakdown.type === 'inclusive' ? 'text-blue-700' : 'text-amber-700'}`}>
                      <span className="font-semibold">Tax Breakdown:</span><br />
                      {taxBreakdown.type === 'inclusive' ? (
                        <>
                          Total: ₹{taxBreakdown.totalPrice.toFixed(2)} (incl. {taxBreakdown.taxRate}% {taxBreakdown.taxName})<br />
                          Base: ₹{taxBreakdown.baseAmount.toFixed(2)} | Tax: ₹{taxBreakdown.taxAmount.toFixed(2)}
                        </>
                      ) : (
                        <>
                          Base: ₹{taxBreakdown.baseAmount.toFixed(2)}<br />
                          Tax: ₹{taxBreakdown.taxAmount.toFixed(2)} | Total: ₹{taxBreakdown.totalPrice.toFixed(2)}
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('features')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Shield className="w-4 h-4 text-purple-500" />
                Features & Limits
              </span>
              {expandedSection === 'features' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'features' && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Students</label>
                    <input
                      type="number"
                      name="maxStudents"
                      min="0"
                      value={formData.maxStudents}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Staff</label>
                    <input
                      type="number"
                      name="maxStaff"
                      min="0"
                      value={formData.maxStaff}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Branches</label>
                    <input
                      type="number"
                      name="maxBranches"
                      min="1"
                      value={formData.maxBranches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Max Admins</label>
                    <input
                      type="number"
                      name="maxAdmins"
                      min="1"
                      value={formData.maxAdmins}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Storage (GB)</label>
                    <input
                      type="number"
                      name="maxStorageGB"
                      min="0"
                      value={formData.maxStorageGB}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Visibility Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('visibility')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Eye className="w-4 h-4 text-amber-500" />
                Visibility Settings
              </span>
              {expandedSection === 'visibility' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'visibility' && (
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Visibility</label>
                  <select
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                  >
                    <option value="public">Public - Visible to all tenants</option>
                    <option value="private">Private - Only assigned tenants can see</option>
                    <option value="invitation">Invitation - Invited tenants can see</option>
                  </select>
                </div>

                {formData.visibility === 'private' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <span className="font-semibold">ℹ️ Tenant Assignment:</span><br />
                      {formData.assignedTenants.length > 0 ? (
                        <>This plan is assigned to <strong>{formData.assignedTenants.length}</strong> tenant(s). 
                        To manage assignments, go to the <strong>Tenants</strong> tab.</>
                      ) : (
                        <>No tenants assigned yet. Go to the <strong>Tenants</strong> tab to assign tenants to this private plan.</>
                      )}
                    </p>
                  </div>
                )}

                {formData.visibility === 'invitation' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <span className="font-semibold">ℹ️ Invitation Management:</span><br />
                      {formData.invitedTenants.length > 0 ? (
                        <>This plan has <strong>{formData.invitedTenants.length}</strong> invited tenant(s).</>
                      ) : (
                        <>No invitations sent yet.</>
                      )}
                    </p>
                  </div>
                )}

                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  Show in public listings
                </label>
              </div>
            )}
          </div>

          {/* Modules Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('modules')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Lock className="w-4 h-4 text-amber-500" />
                Modules ({moduleCount} of {moduleKeys.length})
              </span>
              {expandedSection === 'modules' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'modules' && (
              <div className="px-4 pb-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs text-slate-500">{moduleCount} modules enabled</p>
                  <button
                    type="button"
                    onClick={() => {
                      const allEnabled = moduleKeys.every(key => formData.modules[key]);
                      const newModules = {};
                      moduleKeys.forEach(key => {
                        newModules[key] = !allEnabled;
                      });
                      console.log('📝 [EditPlanModal] Toggling all modules:', newModules);
                      setFormData(prev => ({ ...prev, modules: newModules }));
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {moduleKeys.every(key => formData.modules[key]) ? 'Disable All' : 'Enable All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {moduleKeys.map((key) => {
                    const value = Boolean(formData.modules?.[key]);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleModuleToggle(key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-all text-left ${
                          value
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {value ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Lock className="w-3.5 h-3.5 text-slate-400" />}
                        <span className="capitalize truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Roles Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('roles')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Shield className="w-4 h-4 text-purple-500" />
                Roles ({enabledRolesCount} of {roleKeys.length})
              </span>
              {expandedSection === 'roles' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'roles' && (
              <div className="px-4 pb-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs text-slate-500">{enabledRolesCount} roles enabled</p>
                  <button
                    type="button"
                    onClick={() => {
                      const allEnabled = roleKeys.length > 0 && roleKeys.every((key) => Boolean(formData.roleAccess?.[key]));
                      const newRoles = {};
                      roleKeys.forEach(key => {
                        newRoles[key] = !allEnabled;
                      });
                      console.log('📝 [EditPlanModal] Toggling all roles:', newRoles);
                      setFormData(prev => ({ ...prev, roleAccess: newRoles }));
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {roleKeys.length > 0 && roleKeys.every((key) => Boolean(formData.roleAccess?.[key])) ? 'Revoke All' : 'Grant All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {roleKeys.map((key) => {
                    const value = Boolean(formData.roleAccess?.[key]);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleRoleToggle(key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-all text-left ${
                          value
                            ? 'bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {value ? <Shield className="w-3.5 h-3.5 text-purple-500" /> : <Lock className="w-3.5 h-3.5 text-slate-400" />}
                        <span className="truncate">{key}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ✅ FIXED: Trial Section */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('trial')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Gift className="w-4 h-4 text-blue-500" />
                Trial Settings
              </span>
              {expandedSection === 'trial' ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {expandedSection === 'trial' && (
              <div className="px-4 pb-4 space-y-3">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    name="trialEnabled"
                    checked={formData.trialEnabled}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  Enable Free Trial                </label>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Trial Duration (Days)
                    {!formData.trialEnabled && (
                      <span className="text-slate-400 ml-1">(Enable trial to set duration)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="trialDays"
                    min="1"
                    max="90"
                    value={formData.trialDays}
                    onChange={handleChange}
                    disabled={!formData.trialEnabled}
                    className={`w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white ${
                      !formData.trialEnabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  {formData.trialEnabled && (
                    <p className="text-xs text-emerald-600 mt-1">
                      ✅ Trial will be enabled for {formData.trialDays} days
                    </p>
                  )}
                  {!formData.trialEnabled && (
                    <p className="text-xs text-slate-400 mt-1">
                      ⚠️ Trial is currently disabled
                    </p>
                  )}
                </div>
                
                {formData.trialEnabled && formData.trialDays > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <span className="font-semibold">ℹ️ Trial Configuration:</span><br />
                      • Tenants can try this plan for <strong>{formData.trialDays} days</strong><br />
                      • Limited features will apply during trial<br />
                      • Each tenant can start trial once
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Toggles */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              Active
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPlanModal;